import { useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, ArrowRight, CheckCircle, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const assessmentQuestions = [
  "Does your loved one need help with daily activities (bathing, dressing, eating)?",
  "Is 24/7 supervision or overnight care required?",
  "Are there memory care needs (Alzheimer's, dementia, or cognitive decline)?",
  "Is mobility assistance or fall prevention needed?",
  "Would your loved one benefit from a small, home-like environment rather than a large facility?",
];

export function CareFitAssessment() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [showResults, setShowResults] = useState(false);

  const handleAnswer = (answer: boolean) => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);

    if (currentQuestion < assessmentQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const resetAssessment = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResults(false);
  };

  const yesCount = answers.filter(Boolean).length;
  const isGoodFit = yesCount >= 3;

  return (
    <Dialog onOpenChange={(open) => !open && resetAssessment()}>
      <DialogTrigger asChild>
        <button className="group flex flex-col items-center gap-4 rounded-2xl border border-border/50 bg-card p-6 sm:p-8 shadow-[var(--shadow-card)] transition-all duration-300 hover:border-accent/30 hover:shadow-[0_0_25px_5px_hsl(var(--accent)/0.4)] hover:-translate-y-1 text-center w-full">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 transition-all duration-300 group-hover:bg-primary/20 group-hover:scale-110">
            <ClipboardCheck className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground">
              Care Level Fit Assessment
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Answer 5 quick questions to see if we're the right fit
            </p>
          </div>
          <span className="inline-flex items-center gap-2 text-sm font-medium text-primary">
            Take Assessment
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {showResults ? "Your Assessment Results" : "Care Level Fit Assessment"}
          </DialogTitle>
        </DialogHeader>

        {!showResults ? (
          <div className="py-6">
            <div className="mb-6">
              <div className="flex justify-between text-sm text-muted-foreground mb-2">
                <span>Question {currentQuestion + 1} of {assessmentQuestions.length}</span>
                <span>{Math.round(((currentQuestion) / assessmentQuestions.length) * 100)}% complete</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${((currentQuestion) / assessmentQuestions.length) * 100}%` }}
                />
              </div>
            </div>

            <p className="text-foreground text-lg leading-relaxed mb-8">
              {assessmentQuestions[currentQuestion]}
            </p>

            <div className="flex gap-4">
              <Button
                onClick={() => handleAnswer(true)}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                Yes
              </Button>
              <Button
                onClick={() => handleAnswer(false)}
                variant="outline"
                className="flex-1"
              >
                No
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-6 text-center">
            <div className={`mx-auto mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full ${isGoodFit ? 'bg-accent/10' : 'bg-muted'}`}>
              <CheckCircle className={`h-10 w-10 ${isGoodFit ? 'text-accent' : 'text-muted-foreground'}`} />
            </div>

            <h3 className="font-heading text-xl font-semibold text-foreground mb-4">
              {isGoodFit
                ? "We may be a great fit for your family!"
                : "Let's discuss your specific needs"}
            </h3>

            <p className="text-muted-foreground mb-8">
              {isGoodFit
                ? "Based on your answers, our adult family home could provide the care and support your loved one needs. We'd love to show you our home and discuss how we can help."
                : "Every family's situation is unique. We'd be happy to speak with you to better understand your needs and see if we can help."}
            </p>

            <div className="flex flex-col gap-3">
              <Link
                to="/contact#tour-form"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90"
              >
                Schedule a Private Tour
                <ArrowRight className="h-4 w-4" />
              </Link>
              <DialogClose asChild>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Close
                </button>
              </DialogClose>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
